import React, { useState, useRef, KeyboardEvent } from 'react';
import { Box, IconButton, Popper, Paper, MenuList, MenuItem, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import { TextField } from '@mui/material';

export type CommandSuggestion = {
  command: string;
  description: string;
};

type Props = {
  onSubmit: (message: string) => void;
  onCancel?: () => void;
  disabled?: boolean;
  isProcessing?: boolean;
  placeholder?: string;
  suggestions?: CommandSuggestion[];
};

const MessageInput: React.FC<Props> = ({
  onSubmit,
  onCancel,
  disabled,
  isProcessing,
  placeholder = 'Type a message...',
  suggestions = [],
}) => {
  const [input, setInput] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const showSuggestions = input.startsWith('/') && suggestions.length > 0;

  const filteredSuggestions = suggestions.filter((s) =>
    s.command.toLowerCase().includes(input.slice(1).toLowerCase())
  );

  const openPopper = Boolean(anchorEl) && showSuggestions && filteredSuggestions.length > 0;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
    setAnchorEl(e.currentTarget);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !isProcessing && !disabled) {
        onSubmit(input.trim());
        setInput('');
      }
    }
  };

  const handleSuggestionClick = (command: string) => {
    setInput('/' + command + ' ');
    inputRef.current?.focus();
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Popper open={openPopper} anchorEl={anchorEl} placement="top-start" sx={{ zIndex: 1300, width: anchorEl?.clientWidth }}>
        <Paper elevation={3} sx={{ mb: 1, maxHeight: 200, overflowY: 'auto' }}>
          <MenuList>
            {filteredSuggestions.map((suggestion) => (
              <MenuItem key={suggestion.command} onClick={() => handleSuggestionClick(suggestion.command)}>
                <Typography variant="body2" fontWeight="bold" sx={{ mr: 1, color: 'primary.main' }}>
                  /{suggestion.command}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {suggestion.description}
                </Typography>
              </MenuItem>
            ))}
          </MenuList>
        </Paper>
      </Popper>

      <TextField
        inputRef={inputRef}
        fullWidth
        multiline
        maxRows={4}
        value={input}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        InputProps={{
          endAdornment: isProcessing ? (
            <IconButton onClick={onCancel} color="secondary" size="small">
              <StopIcon />
            </IconButton>
          ) : (
            <IconButton 
              onClick={() => {
                if (input.trim() && !disabled) {
                  onSubmit(input.trim());
                  setInput('');
                }
              }}
              color="primary"
              disabled={disabled || !input.trim()}
              size="small"
            >
              <SendIcon />
            </IconButton>
          ),
          sx: {
            borderRadius: 2.5,
            bgcolor: 'background.paper',
            pr: 0.75,
            py: 0.25,
            fontSize: '0.85rem',
            '& .MuiInputBase-input': {
              py: 0.5,
            },
          },
        }}
      />
    </Box>
  );
};

export default MessageInput;
